import {
    type CartItemType,
    type CartMoney,
    type CartPrice,
    type CartType,
    type ImageType,
} from '@jsmdg/cart-library-ts';
import {
    type Cart as TrackingCart,
    type CartItem,
    type Money,
    type PaymentMethod,
    type Price,
    type ProductType,
} from '@jsmdg/tracking';

type Image = {
    alt: string;
    url: string;
};

const mapToImage = (imageWithImgix: ImageType): Image => {
    return {
        url: imageWithImgix.src,
        alt: imageWithImgix.alt,
    };
};

const mapMoney = (money: CartMoney): Money => {
    return {
        amount: Number(money.amount),
        currencyCode: money.currencyCode,
    };
};

const mapPrice = (price: CartPrice): Price => {
    return {
        gross: mapMoney(price.gross),
    };
};

const mapCartItems = (items: CartItemType[]): CartItem[] => {
    return items.map(item => {
        return {
            id: item.itemId,
            quantity: item.quantity,
            total: mapPrice(item.total),
            selectedPackaging: {
                ...item.selectedPackaging,
                price: mapPrice(item.selectedPackaging.price),
                image: mapToImage(item.selectedPackaging.image),
                thumbnail: mapToImage(item.selectedPackaging.thumbnail),
            },
            availablePackagingOptions: item.availablePackagingOptions.map(option => ({
                ...option,
                price: mapPrice(option.price),
                description: '',
                image: { alt: '', url: '' },
                thumbnail: { alt: '', url: '' },
            })),
            product: {
                ...item.product,
                trackingName: item.product.trackingName ?? '',
                trackingPath: item.product.trackingPath ?? [],
                manufacturerSku: item.product.manufacturerSku,
                productType: item.product.productType as unknown as ProductType,
                price: mapPrice(item.product.price),
                originalPrice: item.product.originalPrice
                    ? mapPrice(item.product.originalPrice)
                    : undefined,
                image: mapToImage(item.product.image),
            },
        };
    });
};

export const mapToTrackingCart = (cart: CartType): TrackingCart => {
    return {
        shipping: {
            price: mapPrice(cart.selectedShipping.price),
        },
        total: mapPrice(cart.total),
        selectedPaymentType: (cart.selectedPaymentType ?? undefined) as PaymentMethod | undefined,
        items: mapCartItems(cart.items),
    };
};
